/*
  Oona — Synchronisation de la grille extensible avec le filtrage par variante (app Rubik Variant Images).

  Contexte : l'app Rubik est un app embed (JS global) qui reconnaît UNIQUEMENT le
  markup standard du <slider-component> (.splide__slide). Au changement de variante,
  elle n'y laisse que les médias de la variante, dans son ordre, en les taguant
  data-media-id — et elle peut même injecter des images qui n'existent pas dans
  product.media (copies ré-hébergées). La grille extensible (markup custom) n'est
  pas inspectée par Rubik : elle afficherait donc tout le produit, mal filtré.

  Stratégie : le <slider-component> (même caché en desktop) est la source de vérité
  (déjà filtré + ordonné par Rubik). On RECONSTRUIT la grille extensible directement
  à partir de ses slides (clone des images), en conservant le style de la grille
  (ratio, coins arrondis), puis on applique le découpage « 4 + Voir plus ».
  On ne se synchronise qu'une fois l'ordre du slider STABLE (Rubik réordonne de
  façon asynchrone), sinon la grille refléterait un état intermédiaire.

  Aucun fichier natif touché.

  LIMITATION CONNUE : le ratio « Adaptatif » de la grille extensible n'est pas
  supporté à 100 % (tous les éléments reconstruits prennent le ratio du 1er média).
  Avec un ratio fixe (4/3, 1/1, 4/5), c'est exact. Détails dans buildItemFromSlide().
*/

const INITIAL_COUNT = 4;

class OonaVariantGallerySync {
  constructor(gallery) {
    this.gallery = gallery;
    this.expandable = gallery.querySelector('[data-expandable-gallery]');
    this.initialContainer = this.expandable && this.expandable.querySelector('.product-media-gallery__expandable-initial');
    this.restContainer = this.expandable && this.expandable.querySelector('[data-expandable-rest]');
    this.toggleWrapper = this.expandable && this.expandable.querySelector('[data-expandable-toggle-wrapper]');

    this.badge = null;
    this.itemRounded = false;
    this.itemRatio = '';

    this.sliderObserver = null;
    this.galleryObserver = null;
    this._polling = false;
    this._deadline = 0;
    this._lastSig = null;
    this._stable = 0;
  }

  init() {
    if (!this.expandable || !this.initialContainer) return;
    this.captureTemplate();
    this.observeGalleryReplacement();
    this.attachSliderObserver();
    this.requestSync();
  }

  /* Récupère le style à appliquer aux éléments reconstruits (coins arrondis, ratio)
     et le badge produit, depuis le rendu serveur initial de la grille. */
  captureTemplate() {
    const first = this.expandable.querySelector('.product-media-gallery__expandable-item');
    if (first) {
      this.itemRounded = first.classList.contains('product-media-gallery__expandable-item--rounded');
      const pm = first.querySelector('.product-media');
      if (pm) this.itemRatio = pm.style.getPropertyValue('--ratio');
    }
    const badge = this.expandable.querySelector('.product-badges');
    if (badge) this.badge = badge;
  }

  getSlider() {
    return this.gallery.querySelector('slider-component');
  }

  /* Slides réels (hors clones de boucle) du slider, dédupliqués par media-id, dans l'ordre. */
  getOrderedSlides() {
    const slider = this.getSlider();
    if (!slider) return [];
    const slides = slider.querySelectorAll('.splide__slide[data-media-id]');
    if (!slides.length) return [];
    const seen = new Set();
    const ordered = [];
    slides.forEach((slide) => {
      if (slide.classList.contains('splide__slide--clone')) return;
      const id = slide.getAttribute('data-media-id');
      if (!id || seen.has(id)) return;
      seen.add(id);
      ordered.push(slide);
    });
    return ordered;
  }

  getOrderedSignature() {
    const ordered = this.getOrderedSlides();
    if (!ordered.length) return null;
    return ordered.map((s) => s.getAttribute('data-media-id')).join(',');
  }

  /* Rubik refiltre ET réordonne le slider de façon asynchrone au changement de
     variante. On ne synchronise qu'une fois l'ordre STABLE (inchangé sur 2 lectures
     consécutives), sinon la grille reflète un ordre intermédiaire. Chaque
     déclencheur repousse l'échéance. */
  requestSync() {
    this._deadline = Date.now() + 2000;
    if (!this._polling) {
      this._polling = true;
      this._lastSig = null;
      this._stable = 0;
      this.poll();
    }
  }

  poll() {
    const sig = this.getOrderedSignature();
    if (sig !== this._lastSig) {
      this._lastSig = sig;
      this._stable = 0;
    } else if (sig) {
      this._stable += 1;
    }

    if (sig && this._stable >= 2) {
      this._polling = false;
      this.sync();
      return;
    }
    if (Date.now() < this._deadline) {
      setTimeout(() => this.poll(), 90);
    } else {
      this._polling = false;
      if (sig) this.sync();
    }
  }

  /* Construit un élément de grille à partir d'un slide du slider : on clone le
     média (image/vidéo) et on lui réapplique le ratio configuré de la grille. */
  buildItemFromSlide(slide) {
    const media = slide.querySelector('.product-media');
    if (!media) return null;

    const item = document.createElement('div');
    item.className = 'product-media-gallery__expandable-item';
    if (this.itemRounded) item.classList.add('product-media-gallery__expandable-item--rounded');

    const id = slide.getAttribute('data-media-id');
    if (id) item.setAttribute('data-media-id', id);

    const mediaClone = media.cloneNode(true);
    // LIMITATION CONNUE : on réapplique à TOUS les éléments reconstruits le ratio
    // capturé sur le 1er média de la grille (this.itemRatio). C'est correct avec un
    // ratio FIXE (ex. 4/3, 1/1, 4/5). En revanche, si la grille extensible est réglée
    // en ratio « Adaptatif » (chaque image garde son propre ratio), les éléments
    // reconstruits prendront tous le ratio du 1er média au lieu de leur ratio naturel.
    // Compromis assumé : on reconstruit la grille depuis les slides du slider Rubik
    // (seule source contenant les images injectées par l'app), or ces slides portent
    // le ratio du slider et non le ratio par-média de la grille. Si le mode adaptatif
    // devient nécessaire, dériver le ratio par image via ses attributs width/height.
    if (this.itemRatio) mediaClone.style.setProperty('--ratio', this.itemRatio);
    item.appendChild(mediaClone);

    return item;
  }

  clearItems(container) {
    if (!container) return;
    container.querySelectorAll('.product-media-gallery__expandable-item').forEach((el) => el.remove());
  }

  sync() {
    const slides = this.getOrderedSlides();
    if (!slides.length) return;

    const items = slides.map((slide) => this.buildItemFromSlide(slide)).filter(Boolean);
    if (!items.length) return;

    // Le badge produit suit le premier média (déplacé AVANT de vider les conteneurs).
    if (this.badge && items[0]) items[0].appendChild(this.badge);

    this.clearItems(this.initialContainer);
    this.clearItems(this.restContainer);

    const initialItems = items.slice(0, INITIAL_COUNT);
    const restItems = items.slice(INITIAL_COUNT);

    initialItems.forEach((item) => this.initialContainer.insertBefore(item, this.toggleWrapper || null));
    if (this.restContainer) restItems.forEach((item) => this.restContainer.appendChild(item));

    this.applyToggleState(restItems.length);
  }

  applyToggleState(restCount) {
    if (!this.restContainer) return;
    const isStack = this.expandable.getAttribute('data-view-mode') === 'stack';

    if (restCount === 0) {
      if (this.toggleWrapper) this.toggleWrapper.classList.add('is-hidden');
      this.restContainer.classList.add('is-hidden');
      this.restContainer.setAttribute('hidden', '');
      return;
    }

    if (isStack) {
      if (this.toggleWrapper) this.toggleWrapper.classList.add('is-hidden');
      this.restContainer.classList.remove('is-hidden');
      this.restContainer.removeAttribute('hidden');
    } else {
      if (this.toggleWrapper) this.toggleWrapper.classList.remove('is-hidden');
      this.restContainer.classList.add('is-hidden');
      this.restContainer.setAttribute('hidden', '');
    }
  }

  attachSliderObserver() {
    const slider = this.getSlider();
    if (!slider) return;
    if (this.sliderObserver) this.sliderObserver.disconnect();

    this.sliderObserver = new MutationObserver(() => this.requestSync());
    const target = slider.querySelector('.splide__list') || slider;
    this.sliderObserver.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-media-id'],
    });
  }

  /* Au changement de variante, le thème remplace tout le <slider-component>.
     On ré-attache alors l'observer sur le nouveau slider et on resynchronise. */
  observeGalleryReplacement() {
    this.galleryObserver = new MutationObserver((mutations) => {
      const sliderReplaced = mutations.some((m) =>
        Array.from(m.addedNodes).some(
          (n) => n.nodeType === 1 && (n.tagName === 'SLIDER-COMPONENT' || (n.querySelector && n.querySelector('slider-component')))
        )
      );
      if (sliderReplaced) {
        this.attachSliderObserver();
        this.requestSync();
      }
    });
    this.galleryObserver.observe(this.gallery, { childList: true });
  }
}

function initOonaVariantGallerySync() {
  document.querySelectorAll('product-media-gallery').forEach((gallery) => {
    if (gallery.oonaVariantSync) return;
    if (!gallery.querySelector('[data-expandable-gallery]')) return;
    const instance = new OonaVariantGallerySync(gallery);
    gallery.oonaVariantSync = instance;
    instance.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOonaVariantGallerySync);
} else {
  initOonaVariantGallerySync();
}
